import update from 'immutability-helper';
import { clone } from "lodash";
import { AppStateValue } from "../../AppState";
import { CustomPropertyDataType_LABELS } from "../../custom-properties/utils/constants";
import { getCachedFeature } from "../../shared/cache/FeatureCache";
import { isScalarNumberFilter } from "../../shared/components/customPropertyTicketFilter";
import { TicketProps } from "../../shared/interfaces/ticketproperties.interface";
import { CustomPropertiesAnswerJsonKey, FilterDataType, FILTERS, LICENSE_FEATURE, STATUS_GUID } from "../../shared/utils/constants";
import { filterOptions, formatFilter, formatGroup, formatLifecycle, formatPlatformUser, formatTeam, valueDateFilters, valueNumberFilters } from "../ticketHelper";
import { getChildFiltersFromStorage } from "../toolbar/CustomViewPopup/heplers/customPopupHelper";
import { ticketSearchFilter } from "../../shared/utils/ticketSearchFilter";
import { buildCollectionMemberFilter, buildScalarMemberFilter } from "./assigneeCollaboratorFilter";
import { getNumberQuery } from "./numberFilterQuery";

interface BuildFilterQueryParams {
  state: AppStateValue;
  currentUser: any;
  filterObj: FilterType;
  childFiltervalues: FilterPropsItems[];
  expandedProperties?: FilterExpandDetails;
  valuesCustomViews?: ValuesCustomView;
  includeCustomProperties?: boolean;
  isFromSideViewTickets?: boolean;
}
export const BuildFilterQuery = ({
  state,
  currentUser,
  filterObj,
  childFiltervalues,
  expandedProperties,
  valuesCustomViews,
  includeCustomProperties,
  isFromSideViewTickets,
}: BuildFilterQueryParams) => {
  const claimSupportGroupId = "Cireson.SupportGroup:";
  const claimUserId = "Cireson.Platform.UserId:";

  const claimSupportGroupIdSubstring = claimSupportGroupId.length + 1;
  const claimUserIdSubstring = claimUserId.length + 1;

  const checkStringTypeForQuery = (value: boolean | string | number | object) => typeof value === 'string' ? `'${encodeURIComponent(value)}'` : `${value}`;
  const appendFilterQueryIfExists = (filterQuery: string) => filterQuery ? ' and ' + filterQuery : '';
  const getStatusIdByGuid = (statusGuid: string) => {
    return state.ticketStatus.find((status: TicketProps) => status.Guid === statusGuid)?.Id;
  }


  const getDatesQuery = (selectedValuesArr: string[], dateTitle: 'DueDate' | 'ResolutionDate' | 'CreatedDate' | 'ModifiedDate' | 'CustomDate' | 'CustomTime', childFiltervalues?: FilterPropsItems[], dateCustomView?: ValuesCustomView) => {
    let getCurrentDateTime = new Date();
    let today = new Date(
      getCurrentDateTime.getUTCFullYear(),
      getCurrentDateTime.getMonth(),
      getCurrentDateTime.getDate()
    );

    let tomorrow = new Date(
      getCurrentDateTime.getUTCFullYear(),
      getCurrentDateTime.getMonth(),
      getCurrentDateTime.getDate() + 1
    );

    const _getQuery = (acc: string, condition: string, isCustomDate?: boolean) => {
      const dateCondition = isCustomDate ? condition : `(${condition}`;
      return acc ? acc + ` or ${condition}` : dateCondition;
    };

    const modifiedDateValueFromCustomView = dateCustomView?.ModifiedDate;
    const createdDateValueFromCustomView = dateCustomView?.CreatedDate;
    const customDatesFromCustomView = dateCustomView?.CustomValues ?? {};

    let query;
    if (['ModifiedDate', 'CreatedDate', 'CustomDate'].includes(dateTitle)) {
      const cpDateKey = Object.keys(customDatesFromCustomView)?.at(0) ?? "";
      const date = childFiltervalues?.find(x => x.key == (dateTitle === 'CustomDate' ? cpDateKey : dateTitle)).values?.find(x => x.selected)?.date;
      const modDateCondition = dateTitle === 'ModifiedDate' ? modifiedDateValueFromCustomView : createdDateValueFromCustomView;
      let viewDate = dateTitle === 'CustomDate' ? customDatesFromCustomView[cpDateKey] : modDateCondition;
      if (date || viewDate) {
        query = selectedValuesArr.reduce((acc: string, current: any) => {
          switch (current) {
            case 0:
              acc = _getQuery(acc, `${dateTitle === 'CustomDate' ? cpDateKey : dateTitle} lt ${new Date(date ?? viewDate).toISOString()}`, dateTitle === 'CustomDate');
              break;
            case 1:
              acc = _getQuery(acc, `${dateTitle === 'CustomDate' ? cpDateKey : dateTitle} le ${getDateTillEndOfTheDay(date ?? viewDate)}`, dateTitle === 'CustomDate');
              break;
            case 2:
              acc = _getQuery(acc, `${dateTitle === 'CustomDate' ? cpDateKey : dateTitle} gt ${getDateTillEndOfTheDay(date ?? viewDate)}`, dateTitle === 'CustomDate');
              break;
            case 3:
              acc = _getQuery(acc, `${dateTitle === 'CustomDate' ? cpDateKey : dateTitle} ge ${new Date(date ?? viewDate).toISOString()}`, dateTitle === 'CustomDate');
              break;
            default: break;
          }
          return acc;
        }, '');
      }
    } else if (dateTitle === 'CustomTime') {
      const cpDateKey = Object.keys(customDatesFromCustomView)?.at(0) ?? "";
      const date = childFiltervalues?.find(x => x.key == cpDateKey)?.values?.find(x => x.selected)?.date;
      let viewDate = customDatesFromCustomView[cpDateKey];

      if (date || viewDate) {
        query = selectedValuesArr.reduce((acc: string, current: any) => {
          switch (current) {
            case 0:
              acc = _getQuery(acc, `${cpDateKey} lt ${date ?? viewDate}`, true);
              break;
            case 1:
              acc = _getQuery(acc, `${cpDateKey} le ${date ?? viewDate}`, true);
              break;
            case 2:
              acc = _getQuery(acc, `${cpDateKey} gt ${date ?? viewDate}`, true);
              break;
            case 3:
              acc = _getQuery(acc, `${cpDateKey} ge ${date ?? viewDate}`, true);
              break;
            default: break;
          }
          return acc;
        }, '');
      }
    } else {
      query = selectedValuesArr.reduce((acc: string, current: string) => {
        switch (parseInt(current, 10)) {
          case 0:
            acc = _getQuery(acc, `${dateTitle} lt ${today.toJSON()}`);
            break;
          case 1:
            acc = _getQuery(acc, `${dateTitle} eq ${today.toJSON()}`);
            break;
          case 2:
            acc = _getQuery(acc, `${dateTitle} eq ${tomorrow.toJSON()}`);
            break;
          case 3:
            acc = _getQuery(acc, `(${dateTitle} ge ${getFirstDayOfWeek().toJSON()}) and (${dateTitle} lt ${getFirstDayNextWeek().toJSON()})`);
            break;
          case 4:
            acc = _getQuery(acc, `(${dateTitle} ge ${getFirstDayNextWeek().toJSON()}) and (${dateTitle} lt ${getLastDayNextWeek().toJSON()})`);
            break;
          case 5:
            acc = _getQuery(acc, `${dateTitle} gt ${today.toJSON()}`);
            break;
          case 6:
            acc = _getQuery(acc, `${dateTitle} eq null`);
            break;
          default: break;
        }
        return acc;
      }, '');
    }
    return query;
  };


  // initially filterObj contains all values of dropdown filters as empty array
  let nonEmptyFilterQueryCount = 0; // if we do not select any custom filters then their values will be empty array
  let cpQueryArr = [];
  const cpReplaceKey = `${CustomPropertiesAnswerJsonKey}.`;
  const assigneeIdsOrcollaboratorIds: any = {};
  const requesterIdsOrAffectedUserIds: any = {};
  const useInQuery = ["TeamId", "SupportGroupId"];

  let filterQuery = filterObj && Object.entries(filterObj).reduce((acc, current) => {

    const [title, selectedValues] = current as [string, any[]];
    let _selectedValues = selectedValues;
    let _title = title;

    //My Team or My Group
    if (["TeamId", "SupportGroupId", "RequesterId", "AssigneeId", "c: c/PlatformUserId", "a: a/PlatformUserId"].includes(title)) {
      let claimsId = "";
      let claimsSub = 0;

      switch (title) {
        case "SupportGroupId":
          claimsId = claimSupportGroupId;
          claimsSub = claimSupportGroupIdSubstring;
          break;
        case "AssigneeId":
        case "RequesterId":
        case "c: c/PlatformUserId":
        case "a: a/PlatformUserId":
          claimsId = claimUserId;
          claimsSub = claimUserIdSubstring;
          break;
      }

      selectedValues.forEach(item => {
        if (item == -1) {
          if (title != "TeamId") {
            _selectedValues.push(...currentUser.Claims.filter(x => x.startsWith(claimsId)).map(x => parseInt(x.substring(claimsSub))));
          }
          else {
            const claimKey = "groups: ";
            let groups = currentUser.Claims.filter(x => x.startsWith(claimKey)).map(x => x.substring(claimKey.length))
            let myTeams = state.teams.filter(x => groups.includes(x.TeamsAadObjectId)).map(x => x.Id);
            _selectedValues.push(...myTeams);
          }
        }
      });

      //this will remove the id=-1
      let removeNegativeValue = _selectedValues.findIndex(x => x == -1);
      if (!useInQuery.includes(title) && removeNegativeValue != -1) {
        _selectedValues.splice(removeNegativeValue, 1);
      }
    }

    if (expandedProperties?.properties.includes(title)) _title = `${expandedProperties.name}/${title}`;

    if (['DueDate', 'ResolutionDate', 'CreatedDate', 'ModifiedDate'].includes(title)) return acc;
    if (isScalarNumberFilter({ key: title } as FilterPropsItems)) {
      const numberQuery = getNumberQuery(_selectedValues, childFiltervalues, {
        ...valuesCustomViews,
        CustomValues: { [title]: valuesCustomViews?.CustomValues?.[title] }
      });
      if (!numberQuery) return acc;

      acc = nonEmptyFilterQueryCount > 0 ? `${acc} and ` : acc;
      nonEmptyFilterQueryCount++;
      return `${acc}(${numberQuery})`;
    }

    if (!title.startsWith(cpReplaceKey))
      acc = nonEmptyFilterQueryCount > 0 && _selectedValues.length > 0 ? `${acc} and ` : acc;


    // if dropdownfilter is not an empty array
    if (_selectedValues.length > 0) {
      nonEmptyFilterQueryCount++;

      let query = "";
      let cpQuery = "";
      let currentProp = childFiltervalues.find(x => x.key == title || x.key + "Id" == title);
      if (["AssigneeId", "c: c/PlatformUserId"].includes(title)) {
        //this will make a token for AssigneeId and c: c/PlatformUserId to be combine them into a single filter query
        if (!acc.includes("AssigneesOrCollaborators")) {
          acc += `({AssigneesOrCollaborators})`;
        }
        else {
          acc = acc.replace(/\s*and\s*$/, '');
        }
        assigneeIdsOrcollaboratorIds[title] = _selectedValues;
      }
      else if (["RequesterId", "a: a/PlatformUserId"].includes(title)) {
        //this will make a token for AssigneeId and c: c/PlatformUserId to be combine them into a single filter query
        if (!acc.includes("RequesterOrAffectedUsers")) {
          acc += `({RequesterOrAffectedUsers})`;
        }
        else {
          acc = acc.replace(/\s*and\s*$/, '');
        }
        requesterIdsOrAffectedUserIds[title] = _selectedValues;
      }
      else if (title.startsWith(cpReplaceKey)) {
        if (currentProp.FilterDataType == FilterDataType.Date || currentProp.FilterDataType == FilterDataType.Time) {
          const findDateValue = valuesCustomViews?.CustomValues[title];
          if (findDateValue)
            query = `${getDatesQuery(filterObj[title], currentProp.FilterDataType == FilterDataType.Date ? 'CustomDate' : 'CustomTime', childFiltervalues, { ...valuesCustomViews, CustomValues: { [title]: findDateValue } })}`;
        }
        else if (currentProp.FilterDataType == FilterDataType.Number) {
          const findNumberValue = valuesCustomViews?.CustomValues[title];
          if (findNumberValue)
            query = `${getNumberQuery(filterObj[title], childFiltervalues, { ...valuesCustomViews, CustomValues: { [title]: findNumberValue } })}`;
        }
        else if (_selectedValues.length === 1) {
          if (currentProp?.FilterDataType == FilterDataType.String) {
            query = `${_title} eq '${encodeURIComponent(_selectedValues[0]["value"])}'`;
          }
          else if (currentProp?.FilterDataType == FilterDataType.Boolean) {
            query = `${_title} eq ${_selectedValues[0] == 1}`;
          }
          else {
            query = `${_title} eq ${checkStringTypeForQuery(_selectedValues[0])}`;
          }
        } else
          query = `${_title} eq '${_selectedValues.map(x => checkStringTypeForQuery(x)).join(',')}'`;

        cpQuery += query;
        cpQueryArr.push(cpQuery);
      }
      else {
        if (!useInQuery.includes(title) && _selectedValues.length === 1) {
          let currentProp = childFiltervalues.find(x => x.key == title || x.key + "Id" == title);
          if (currentProp?.FilterDataType == FilterDataType.String) {

            query = `contains(${_title}, '${encodeURIComponent(_selectedValues[0]["value"])}')`;
          }
          else if (currentProp?.FilterDataType == FilterDataType.Boolean) {
            query = `${_title} eq ${_selectedValues[0] == 1}`;
          }
          else {
            query = `${_title} eq ${checkStringTypeForQuery(_selectedValues[0])}`;
          }
        }
        else {
          query = `${_title} in (${_selectedValues.map(x => checkStringTypeForQuery(x)).join(',')})`;
        }

        if (title === "t: t/Id")
          query = `Tags/any(${query})`;
        else if (title === "Resolved")
          query = `StatusId ne ${getStatusIdByGuid(STATUS_GUID.RESOLVED)}`
        else if (title === "a: a/PlatformUserId")
          query = `AffectedUsers/any(${query.replace("-1", state.currentUserId.toString())})`
        else if (title === "AssigneeOrCollaborator")
          query = `AssigneeId eq ${checkStringTypeForQuery(_selectedValues[0])} or TicketCollaborators/any(c: c/PlatformUserId eq ${checkStringTypeForQuery(_selectedValues[0])})`

        acc += `(${query})`;
      }
    }
    return acc;
  }, '');


  const queryValue = encodeURIComponent(state.filterDropdown.queryValue); // value entered in input field for searching values based on Request Column
  if (!isFromSideViewTickets && queryValue) {
    filterQuery = ticketSearchFilter(queryValue, { matchId: true, comments: 'all' }) + appendFilterQueryIfExists(filterQuery);
  }

  if (filterObj['DueDate']?.length > 0) {
    filterQuery = `${getDatesQuery(filterObj['DueDate'], 'DueDate')})${appendFilterQueryIfExists(filterQuery)}`;
  }

  if (filterObj['ResolutionDate']?.length > 0) {
    filterQuery = `${getDatesQuery(filterObj['ResolutionDate'], 'ResolutionDate')})${appendFilterQueryIfExists(filterQuery)}`;
  }
  if (filterObj['ModifiedDate']?.length > 0) {
    filterQuery = `${getDatesQuery(filterObj['ModifiedDate'], 'ModifiedDate', childFiltervalues, valuesCustomViews)})${appendFilterQueryIfExists(filterQuery)}`;
  }
  if (filterObj['CreatedDate']?.length > 0) {
    filterQuery = `${getDatesQuery(filterObj['CreatedDate'], 'CreatedDate', childFiltervalues, valuesCustomViews)})${appendFilterQueryIfExists(filterQuery)}`;
  }
  if (filterQuery === 'undefined)') {
    filterQuery = '';
  }

  //this will combine the AssigneeId and c: c/PlatformUserId (Collaborator) into a single filter query
  if (assigneeIdsOrcollaboratorIds && Object.keys(assigneeIdsOrcollaboratorIds).length > 0) {
    const assigneeIds = assigneeIdsOrcollaboratorIds["AssigneeId"] ?? [];
    const collaboratorIds = assigneeIdsOrcollaboratorIds["c: c/PlatformUserId"] ?? [];

    const filter = [
      buildCollectionMemberFilter("TicketCollaborators", "c", "c/PlatformUserId", collaboratorIds),
      buildScalarMemberFilter("AssigneeId", assigneeIds)
    ].filter(Boolean).join(" or ");

    filterQuery = filterQuery.replace(/\({AssigneesOrCollaborators}\)/g, `(${filter})`);
  }
  else {
    filterQuery = filterQuery.replace(/\({AssigneesOrCollaborators}\)/g, "");
  }

  if (requesterIdsOrAffectedUserIds && Object.keys(requesterIdsOrAffectedUserIds).length > 0) {
    const requeserIds = requesterIdsOrAffectedUserIds["RequesterId"] ?? [];
    const affectedUserIds = requesterIdsOrAffectedUserIds["a: a/PlatformUserId"] ?? [];
    const filter = [
      buildCollectionMemberFilter("AffectedUsers", "a", "a/PlatformUserId", affectedUserIds),
      buildScalarMemberFilter("RequesterId", requeserIds)
    ].filter(Boolean).join(" or ");
    filterQuery = filterQuery.replace(/\({RequesterOrAffectedUsers}\)/g, `(${filter})`);
  }

  return { main: filterQuery, custom: includeCustomProperties ? cpQueryArr.join(' and ') : [] };
};

export const getDateTillEndOfTheDay = (modifiedDate: string) => {
  let date = new Date(modifiedDate);
  date.setHours(23, 59, 59, 999);
  return date.toISOString();
}

export const getFirstDayOfWeek = () => {
  const today = new Date();
  return new Date(
    today.getUTCFullYear(),
    today.getMonth(),
    today.getDate() - today.getDay()
  );
};

export const getFirstDayNextWeek = () => {
  const today = new Date();
  const daysToAdd = 7 - today.getDay();
  return new Date(
    today.getUTCFullYear(),
    today.getMonth(),
    today.getDate() + daysToAdd
  );
};

export const getLastDayNextWeek = () => {
  const today = new Date();
  const daysToAdd = 14 - today.getDay();
  return new Date(
    today.getUTCFullYear(),
    today.getMonth(),
    today.getDate() + daysToAdd
  );
};

const title: { [key: string]: string } = {
  Assignee: 'AssigneeId',
  Group: 'SupportGroupId',
  'Ticket Type': 'TicketTypeId',
  Due: 'DueDate',
  Lifecycle: 'TicketLifecycle/LifecycleId',
  Resolution: 'ResolutionDate',
  CreatedDate: 'CreatedDate',
  ModifiedDate: 'ModifiedDate',
  Tags: 't: t/Id',
  Requester: 'RequesterId',
  GroupChat: 'GroupChat/Id',
  Collaborators: 'c: c/PlatformUserId',
  'Affected Users': 'a: a/PlatformUserId',
  DraftKB: "Resolution/DraftKB",
  ResolutionCategory: "Resolution/ResolutionCategoryId",
  Note: "Resolution/Note",
  InternalNote: "Resolution/InternalNote",
  AssigneesOrCollaborators: "AssigneesOrCollaborators",
  RequesterOrAffectedUsers: "RequesterOrAffectedUsers",
  // Identity, and it still has to be listed: an unmapped key falls through to the `${key}Id`
  // default, so the query would ask the server for TotalTimeSpentMinutesId.
  TotalTimeSpentMinutes: 'TotalTimeSpentMinutes',
};

export const objectFromSelectedFilters = (selectedFilterDropdownValues: FilterPropsItems[], isChild: boolean) => {
  let modifiedDateCustomView = null;
  let createdDateCustomView = null;
  let customValueCustomView = {};
  const customFilters: FilterType = selectedFilterDropdownValues.reduce((acc, current) => {
    if (current.stringValue) {
      const customFilter = { [current.key]: [{ value: current.stringValue }] };
      acc = { ...acc, ...customFilter };
    }
    else {
      const titleKey = current.key.startsWith(`${CustomPropertiesAnswerJsonKey}.`) ? current.key : title[current.key] || `${current.key}Id`;
      const selectedValue = current.values.filter((value) => value.selected).map((value) => {
        if (!isChild && current.title === FILTERS.MODIFIED_DATE.title && !modifiedDateCustomView)
          modifiedDateCustomView = value.date;

        if (!isChild && current.title === FILTERS.CREATED_DATE.title && !createdDateCustomView)
          createdDateCustomView = value.date;

        if ((current.FilterDataType == FilterDataType.Date || current.FilterDataType == FilterDataType.Time) && !customValueCustomView[titleKey])
          customValueCustomView[titleKey] = value.date;

        if ((current.FilterDataType == FilterDataType.Number) && !customValueCustomView[titleKey])
          customValueCustomView[titleKey] = value.number;

        // Due, Resolution, Created, Modified filter
        if (current.title === FILTERS.DUE.title || current.title === FILTERS.RESOLUTION.title || current.title === FILTERS.CREATED_DATE.title || current.title === FILTERS.MODIFIED_DATE.title || current.FilterDataType == FilterDataType.Date || current.FilterDataType == FilterDataType.Time || current.FilterDataType == FilterDataType.Number)
          return value.key;

        return value.key === 0 ? null : value.key;
      });

      const customFilter = { [titleKey]: selectedValue };
      acc = { ...acc, ...customFilter };
    }

    return acc;
  }, {} as FilterType);

  return { customFilters, modifiedDateCustomView, createdDateCustomView, customValueCustomView };
};

export const prepareFiltersData = async (results, currentstate: AppStateValue, t) => {
  // Update filters based on Status, Ticket Type, Category, Priority, Users, and Groups data
  // Status
  //const [filters, setFilters] = useState<FilterPropsItems[]>(filterOptions);
  let filters = clone(filterOptions);
  let index = filters.findIndex(x => x.id == FILTERS.STATUS.id);
  let updateStatusFilter = update(filters[index], { values: { $set: formatFilter(results.ticketStatus) } });
  let newFilters: FilterPropsItems[] = update(filters, { $splice: [[index, 1, updateStatusFilter]] });

  // Priority
  index = filters.findIndex(x => x.id == FILTERS.PRIORITY.id);
  let updatePriorityFilter = update(filters[index], { values: { $set: formatFilter(results.priority) } });
  newFilters = update(newFilters, { $splice: [[index, 1, updatePriorityFilter]] });

  // Category
  index = filters.findIndex(x => x.id == FILTERS.CATEGORY.id);
  let updateCategoryFilter = update(filters[index], { values: { $set: formatFilter(results.categories) } });
  newFilters = update(newFilters, { $splice: [[index, 1, updateCategoryFilter]] });

  // Ticket Type
  index = filters.findIndex(x => x.id == FILTERS.TICKET_TYPE.id);
  let updateTicketTypeFilter = update(filters[index], { values: { $set: formatFilter(results.ticketTypes) } });
  newFilters = update(newFilters, { $splice: [[index, 1, updateTicketTypeFilter]] });

  // Team
  index = filters.findIndex(x => x.id == FILTERS.TEAM.id);
  let updateTeamFilters = update(filters[index], { values: { $set: formatTeam(results.teams) } });
  newFilters = update(newFilters, { $splice: [[index, 1, updateTeamFilters]] });

  // Group
  index = filters.findIndex(x => x.id == FILTERS.GROUP.id);
  let updateGroupFilters = update(filters.find(x => x.id == FILTERS.GROUP.id), { values: { $set: formatGroup(results.supportgroups) } });
  newFilters = update(newFilters, { $splice: [[index, 1, updateGroupFilters]] });

  // Assignee
  index = filters.findIndex(x => x.id == FILTERS.ASSIGNEE.id);
  let updateAssigneeFilters = update(filters[index], { values: { $set: formatPlatformUser(results.usersInRole, false, currentstate) } });
  newFilters = update(newFilters, { $splice: [[index, 1, updateAssigneeFilters]] });

  // Tags
  index = filters.findIndex(x => x.id == FILTERS.TAGS.id);
  let updateTagsFilter = update(filters[index], { values: { $set: formatFilter(results.tags) } });
  newFilters = update(newFilters, { $splice: [[index, 1, updateTagsFilter]] });

  // Collaborators
  index = filters.findIndex(x => x.id == FILTERS.TicketCollaborators.id);
  let updateCollaboratorsFilters = update(filters[index], { values: { $set: formatPlatformUser(results.usersInRole, true, currentstate) } });
  newFilters = update(newFilters, { $splice: [[index, 1, updateCollaboratorsFilters]] });

  index = filters.findIndex(x => x.id == FILTERS.LIFECYCLE.id);
  let updateLifecycleFilters = update(filters[index], { values: { $set: formatLifecycle(results.lifecycles) } });
  newFilters = update(newFilters, { $splice: [[index, 1, updateLifecycleFilters]] });

  // Resolution Category
  index = filters.findIndex(x => x.id == FILTERS.ResolutionCategory.id);
  let updateResolutionCategoryFilter = update(filters[index], { values: { $set: formatFilter(results.resolutionCategory) } });
  newFilters = update(newFilters, { $splice: [[index, 1, updateResolutionCategoryFilter]] });

  //Add Custom Properties in custom view filters
  const isCustomPropertiesFeatureEnabled = await getCachedFeature(LICENSE_FEATURE.CustomProperties);
  if (isCustomPropertiesFeatureEnabled) {
    let customPropertyColId = 1000;
    if (!newFilters.some(c => c.id > customPropertyColId)) {
      results?.customProperties.filter(p => !p.IsHidden).toSorted(o => o.Order).forEach(cp => {
        let cpFilter: FilterPropsItems = {
          id: customPropertyColId + cp.Id,
          sortOrder: customPropertyColId + cp.Order,
          title: cp.Name,
          key: `${CustomPropertiesAnswerJsonKey}.${cp.PropertyId}`,
          values: []
        };

        switch (CustomPropertyDataType_LABELS[cp.DataType]) {
          case CustomPropertyDataType_LABELS.DATE:
            cpFilter.FilterDataType = FilterDataType.Date;
            cpFilter.values = [...valueDateFilters];
            break;
          case CustomPropertyDataType_LABELS.TIME:
            cpFilter.FilterDataType = FilterDataType.Time;
            cpFilter.values = [...valueDateFilters];
            break;
          case CustomPropertyDataType_LABELS.STRING:
            cpFilter.FilterDataType = FilterDataType.String;
            break;
          case CustomPropertyDataType_LABELS.INT:
            cpFilter.FilterDataType = FilterDataType.Number;
            cpFilter.values = [...valueNumberFilters];
            break;
          case CustomPropertyDataType_LABELS.BOOL:
            cpFilter.FilterDataType = FilterDataType.Boolean;
            cpFilter.values = [
              {
                key: 1,
                selected: false,
                title: t('ticket.child-filter.true')
              },
              {
                key: 2,
                selected: false,
                title: t('ticket.child-filter.false')
              }];
            break;
          case CustomPropertyDataType_LABELS.LIST:
            cpFilter.values = formatFilter(cp.CustomPropertyListItems);
            break;
          default:
            break;
        }

        newFilters.push(cpFilter);
      });
    }
  }


  const UpdatedFilters = await getUpdatedFilters(newFilters);
  return [UpdatedFilters, newFilters];
};

export const updateSelectedFilters = (presistedValue: FilterAccordionValues, filterValue: FilterAccordionValues) => {
  for (const key in presistedValue) {
    if (Object.hasOwn(presistedValue, key)) {
      const element = filterValue[key];
      if (element !== 'key' && element !== 'title') {
        filterValue[key] = presistedValue[key];
      }
    }
  }
}

export const getUpdatedFilters = async (newFilters: FilterPropsItems[]) => {
  const persistedDropdownValues: FilterPropsItems[] = await getChildFiltersFromStorage();
  if (!persistedDropdownValues) return newFilters;

  // not returning storage value(getChildFiltersFromStorage()) directly, because newFilters can have some more added or deleted filters and storage will not have this update.
  const defaultFilters = JSON.parse(JSON.stringify(newFilters)) as FilterPropsItems[];
  persistedDropdownValues.forEach(persistedFilter => {
    let _filter = defaultFilters.find(x => x.id == persistedFilter.id);
    if (persistedFilter.FilterDataType == FilterDataType.String) {
      _filter.stringValue = persistedFilter.stringValue;
    }
    else {
      persistedFilter.values.forEach(presistedValue => {
        if (presistedValue.selected) {
          let filterValue = _filter.values.find(value => value.key === presistedValue.key);
          // add all updated values from storage except key(which is unique) and title(which can also update and storage can have stale value)
          if (filterValue)
            updateSelectedFilters(presistedValue, filterValue);
          else
            _filter.values.push(presistedValue);
        }
      });
    }
  });
  return defaultFilters;
}